import last from 'lodash/last';

import type { Stay } from '@shared/types/frontend/hotels-deals-response';
import type { FormatDate } from '@shared/types/i18n/localisation-service';
import type { OfferFilters, SearchFilters } from '@shared/types/url-params';
import type { Maybe, Stay as StayShape, Nullable } from '@shared/types/utils';

import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';
import { isBrowser } from '@shared/utils-universal/is-browser';
import { getQueryParam, getIntParam } from '@shared/utils-universal/url/url';

export const getCurrentQueryParam = (parameter: string) => {
  if (isBrowser()) {
    return getQueryParam(window.location.href, parameter) ?? null;
  }
  return null;
};

export const getIsUpSortHotels = (hotelId: string) =>
  getCurrentQueryParam('upsort_hotels')?.split(',')[0] === hotelId;

export const getCurrentIntParam = (name: string) =>
  getIntParam(window.location.href, name);

const pushNewUrl = (url: string) => {
  window.history.pushState(
    {}, // State
    '', // Title, currently not in spec so not used by browsers
    url,
  );
};

type SearchParamsBase = {
  stay?: StayShape | Stay;
  filters?: Nullable<SearchFilters> | Nullable<OfferFilters>;
  destination?: {
    entityId: string;
  };
  freeCancellation?: Maybe<boolean>;
  impressionId?: string;
};

export type SearchParams = SearchParamsBase & {
  url: string;
  clickedDetailsPrice?: number;
  minPriceRoomId?: Nullable<string>;
  poiId?: number;
  searchCycleId?: Nullable<string>;
  priceFilters?: string;
  sort?: string;
  upsortHotels?: number;
  traceInfo?: string;
  extraTraceInfo?: string;
  source?: Nullable<string>;
};

export const getUpdatedSearchParamsUrl = (
  {
    clickedDetailsPrice,
    destination,
    extraTraceInfo,
    filters,
    freeCancellation,
    impressionId,
    minPriceRoomId,
    poiId,
    priceFilters,
    searchCycleId,
    sort,
    source,
    stay,
    traceInfo,
    upsortHotels,
    url,
  }: SearchParams,
  formatDate?: FormatDate,
): string => {
  const { entityId } = destination || {};
  // @ts-expect-error type Stay have no type childrenAges, may be it was difference from two service page define
  const { checkIn, checkOut, childrenAges, numberOfAdults, numberOfRooms } =
    stay || {};
  const urlObj = new URL(url, 'https://www.skyscanner.net');
  const { hash, pathname, searchParams } = urlObj;
  const childrenAgesString = childrenAges && childrenAges.join(',');

  if (entityId) {
    searchParams.set('entity_id', entityId);
    searchParams.delete('s-f_iplace');
  }
  if (checkIn && formatDate) {
    searchParams.set(
      'checkin',
      formatDate(checkIn, DATE_FORMAT.NON_LOCALISED_SHORT),
    );
  }
  if (checkOut && formatDate) {
    searchParams.set(
      'checkout',
      formatDate(checkOut, DATE_FORMAT.NON_LOCALISED_SHORT),
    );
  }
  if (numberOfAdults) {
    searchParams.set('adults', String(numberOfAdults));
  }
  if (numberOfRooms) {
    searchParams.set('rooms', String(numberOfRooms));
  }
  if (childrenAgesString) {
    searchParams.set('children_ages', childrenAgesString);
  } else {
    searchParams.delete('children_ages');
  }
  if (sort) {
    searchParams.set('sort', sort);
  } else {
    searchParams.delete('sort');
  }
  if (searchCycleId) {
    searchParams.set('search_cycle_id', searchCycleId);
  } else {
    searchParams.delete('search_cycle_id');
  }
  if (freeCancellation) {
    searchParams.set('free_cancellation', String(freeCancellation));
  } else {
    searchParams.delete('free_cancellation');
  }
  if (filters && Object.keys(filters).length) {
    searchParams.set('filters', JSON.stringify(filters));
  } else {
    searchParams.delete('filters');
  }
  if (priceFilters) {
    searchParams.set('price_filters', priceFilters);
  } else {
    searchParams.delete('price_filters');
  }
  if (upsortHotels) {
    searchParams.set('upsort_hotels', String(upsortHotels));
  } else {
    searchParams.delete('upsort_hotels');
  }
  if (poiId) {
    searchParams.set('poi_id', String(poiId));
  } else {
    searchParams.delete('poi_id');
  }
  if (impressionId) {
    searchParams.set('impression_id', impressionId);
  }
  if (clickedDetailsPrice) {
    searchParams.set('clicked_details_price', String(clickedDetailsPrice));
  } else {
    searchParams.delete('clicked_details_price');
  }
  if (minPriceRoomId) {
    searchParams.set('min_price_room_id', minPriceRoomId);
  } else {
    searchParams.delete('min_price_room_id');
  }
  if (traceInfo) {
    searchParams.set('trace_info', traceInfo);
  } else {
    searchParams.delete('trace_info');
  }
  if (extraTraceInfo) {
    searchParams.set('extra_trace_info', extraTraceInfo);
  } else {
    searchParams.delete('extra_trace_info');
  }
  if (source) {
    searchParams.set('source', source);
  }

  return `${pathname}?${searchParams.toString()}${hash}`;
};

export const updateSearchParamsUrl = (
  {
    clickedDetailsPrice,
    destination,
    extraTraceInfo,
    filters,
    freeCancellation,
    minPriceRoomId,
    poiId,
    priceFilters,
    searchCycleId,
    sort,
    source,
    stay,
    traceInfo,
    upsortHotels,
  }: Omit<SearchParams, 'url'>,
  formatDate?: FormatDate,
) => {
  const newUrl = getUpdatedSearchParamsUrl(
    {
      url: window.location.href,
      destination,
      stay,
      sort,
      searchCycleId,
      freeCancellation,
      filters,
      priceFilters,
      upsortHotels,
      poiId,
      clickedDetailsPrice,
      minPriceRoomId,
      traceInfo,
      extraTraceInfo,
      source,
    },
    formatDate,
  );

  pushNewUrl(newUrl);
};

export const updateCheckcoutUrl = (url: string, isCheapest: boolean) =>
  typeof window !== 'undefined' && isCheapest ? `${url}&priceFlag=1` : url;

export const getCugOverrideFromUrl = (url: string) =>
  getQueryParam(url, 'cugOverride');

export const getDayviewUrl = (
  {
    destination,
    filters,
    freeCancellation,
    impressionId,
    stay,
  }: SearchParamsBase,
  formatDate: FormatDate,
) => {
  const baseUrl = `/hotels/search`;

  return getUpdatedSearchParamsUrl(
    {
      url: baseUrl,
      destination,
      stay,
      freeCancellation,
      filters,
      impressionId,
    },
    formatDate,
  );
};

export const getPathItems = (url: string): string[] => {
  const { pathname } = new URL(url);

  return pathname.substring(1).split('/');
};

export const verifySearchUrl = (url?: string) =>
  !!url && last(getPathItems(url)) === 'search';

export const verifyDetailsUrl = (url?: string) => {
  if (!url) return false;

  const lastItem = last(getPathItems(url)) || '';

  return /ht-\d/.test(lastItem);
};

export const updateUrl = (paramKey: string, paramValue: string): void => {
  const parsedUrl = new URL(window.location.href);
  const params = parsedUrl.searchParams;

  params.set(paramKey, paramValue);

  window.history.pushState(
    {},
    '',
    `${parsedUrl.pathname}?${params.toString()}`,
  );
};
